import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { Bedrooms } from 'app/shared/types/Bedrooms';
import type { PropertyTypes } from 'app/shared/types/PropertyTypes';
import { handleMarketTrendsFilterChange } from 'app/shared/flux/actions/FilterActions';
import { injectQuery } from 'app/shared/flux/reducers/injectQuery';
import filterUtils from 'app/shared/utils/filterUtils';
import { filterFromQuery } from 'app/shared/utils/filterFromQuery';

import type { bedroomsType, FilterType, propertyTypesType } from 'app/shared/types/Filters';
import type { State } from 'app/shared/types/State';
import type { Dispatch } from '@reduxjs/toolkit';

export function useFilter(): FilterType {
  const search = useSelector((state: State) => state.router.location.search);
  return useMemo(() => filterFromQuery(injectQuery({ search }).query), [search]);
}

export function useMarketTrendsFilters(filter: FilterType, dispatch: Dispatch<any>) {
  const bedrooms = useMemo(() => filterUtils.filterToBedroomsEnum(filter.bedrooms as bedroomsType), [filter]);
  const setBedrooms = useCallback(
    (newBedrooms: Bedrooms) => {
      return dispatch(
        handleMarketTrendsFilterChange(
          {
            bedrooms: filterUtils.bedroomsEnumToFilter(newBedrooms),
            propertyTypes: filter.propertyTypes,
          },
          filter,
        ),
      );
    },
    [dispatch, filter],
  );
  const propertyTypes = useMemo(
    () => filterUtils.filterToPropertyTypesEnum(filter.propertyTypes as propertyTypesType),
    [filter],
  );
  const setPropertyTypes = useCallback(
    (newPropertyTypes: PropertyTypes) => {
      return dispatch(
        handleMarketTrendsFilterChange(
          {
            bedrooms: filter.bedrooms,
            propertyTypes: filterUtils.propertyTypesEnumToFilter(newPropertyTypes),
          },
          filter,
        ),
      );
    },
    [dispatch, filter],
  );
  return { bedrooms, setBedrooms, propertyTypes, setPropertyTypes };
}
